import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { AdminPlanResponse, AdminPlanPayload } from '@utils/types/admin';
import { ADMIN_PLANS_QUERY_KEY } from './use-get-admin-plans';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type UpdateAdminPlanParams = {
  id: number;
  payload: Partial<AdminPlanPayload>;
};

/**
 * Hook to update an existing plan
 *
 * PUT /api/admin/on-prem/plans/{id}
 */
export const useUpdateAdminPlan = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updatePlan = async ({ id, payload }: UpdateAdminPlanParams) => {
    const url = `api/admin/on-prem/plans/${id}`;
    const response = await Axios.put<AdminPlanResponse>(url, payload);
    return response.data;
  };

  return useMutation<
    AdminPlanResponse,
    AxiosError<BaseError>,
    UpdateAdminPlanParams
  >(updatePlan, {
    onSuccess: (_, variables) => {
      toast.success(t('plan-updated-successfully'));
      queryClient.invalidateQueries([ADMIN_PLANS_QUERY_KEY]);
      queryClient.invalidateQueries([ADMIN_PLANS_QUERY_KEY, variables.id]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-update-plan');
      toast.error(errorMessage);
    }
  });
};
